import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { InlineLoadingSpinner } from '@app/components/ui/LoadingSpinner/LoadingSpinner'
import { toast } from '@app/components/ui/Toast/toast'
import { UploadWizardSteps } from './upload-wizard-steps'
import { EditDetailsStep } from './steps/edit-details-step'
import { GeoreferenceStep } from './steps/georeference-step'
import { PermissionsStep } from './steps/permissions-step'
import { useFetchDocumentPermissions, useUpdateDocument } from '../../hooks/useDocuments'
import { EvidenceDocument } from '../../documents.types'
import {
  DocumentEditFormData,
  DocumentEditSchema,
  EDIT_WIZARD_STEPS,
  EditWizardStep,
} from '../../documents-upload.types'

type EditWizardDialogProps = {
  document: EvidenceDocument | null
  open: boolean
  onOpenChange: (open: boolean) => void
}

const STEP_KEYS = Object.keys(EDIT_WIZARD_STEPS) as EditWizardStep[]

// Maps each field to the step its error should be shown on, so a failed save can jump there.
const STEP_FIELDS: Record<EditWizardStep, (keyof DocumentEditFormData)[]> = {
  details: [
    'title',
    'category',
    'descriptionMd',
    'organisation',
    'source',
    'publishedAt',
    'notesMd',
    'tags',
    'collectionId',
  ],
  georeference: ['georeference'],
  permissions: ['visibility', 'roleIds'],
}

function documentToEditFormValues(
  document: EvidenceDocument,
  roleIds: string[],
): DocumentEditFormData {
  return {
    title: document.title,
    category: document.category,
    descriptionMd: document.descriptionMd ?? '',
    organisation: document.organisation ?? '',
    source: document.source ?? '',
    // API returns a full timestamp; the date input needs a bare YYYY-MM-DD.
    publishedAt: document.publishedAt ? document.publishedAt.slice(0, 10) : '',
    notesMd: document.notesMd ?? '',
    tags: document.tags,
    // API omits collection_id (rather than null) when there's no collection.
    collectionId: document.collectionId ?? null,
    georeference: {
      type: document.georeference.type,
      geometry: document.georeference.geometry,
      searchLabel: document.georeference.searchLabel ?? null,
    },
    visibility: document.visibility,
    roleIds,
  }
}

export const EditWizardDialog = ({ document, open, onOpenChange }: EditWizardDialogProps) => {
  const [currentStep, setCurrentStep] = useState<EditWizardStep>('details')
  const updateDocument = useUpdateDocument()

  // Personal-visibility documents have no role rows to fetch — only fetch for
  // organisation-visible ones.
  const shouldFetchPermissions = open && !!document && document.visibility === 'organisation'
  const permissionsQuery = useFetchDocumentPermissions(document?.id, shouldFetchPermissions)
  const isLoadingPermissions = shouldFetchPermissions && permissionsQuery.isPending
  const permissionsFetchFailed = shouldFetchPermissions && permissionsQuery.isError

  // Not gated on isLoadingPermissions — RHF flushes a changed `values` prop one render after
  // the state change that caused it, so gating this crashed EditDetailsStep with every field
  // reading undefined for one render right as the spinner disappeared. roleIds defaults to []
  // until the fetch resolves regardless; the spinner is purely a UX choice.
  const form = useForm<DocumentEditFormData>({
    resolver: zodResolver(DocumentEditSchema),
    mode: 'onChange',
    values: document
      ? documentToEditFormValues(document, permissionsQuery.data?.user_roles ?? [])
      : undefined,
  })

  useEffect(() => {
    if (open) setCurrentStep('details')
  }, [open])

  if (!document) return null

  if (isLoadingPermissions) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-4xl h-[85vh] p-0 flex items-center justify-center">
          <InlineLoadingSpinner />
        </DialogContent>
      </Dialog>
    )
  }

  const currentIndex = STEP_KEYS.indexOf(currentStep)
  const visibility = form.watch('visibility')
  const roleIds = form.watch('roleIds')

  // An organisation-visible document with zero roles selected means nobody but owner/admin can
  // see it — block saving that explicitly, except when the permissions fetch failed, since
  // roleIds:[] there means "couldn't load", not "no roles chosen" (the existingRolesUnknown
  // banner already covers that case).
  const requiresRoleSelection =
    visibility === 'organisation' && roleIds.length === 0 && !permissionsFetchFailed
  const rolesRequiredMessage = 'Select at least one role, or mark this document Private.'

  const canSave = !updateDocument.isPending

  const handleSave = async () => {
    if (requiresRoleSelection) {
      setCurrentStep('permissions')
      return
    }
    const valid = await form.trigger()
    if (!valid) {
      const errorFields = Object.keys(form.formState.errors)
      const firstErrorStep = STEP_KEYS.find((step) =>
        STEP_FIELDS[step].some((field) => errorFields.includes(field)),
      )
      if (firstErrorStep) setCurrentStep(firstErrorStep)
      toast({
        description: 'Fix the highlighted fields before saving.',
        position: 'top-right',
        type: 'error',
      })
      return
    }

    // useUpdateDocument awaits the visibility PATCH before firing the permissions one, so a
    // single Save covers Personal -> Organisation-with-roles.
    const originalRoleIds = permissionsQuery.data?.user_roles ?? []
    const applyRoleChanges =
      visibility !== document.visibility ||
      roleIds.length !== originalRoleIds.length ||
      roleIds.some((id) => !originalRoleIds.includes(id))

    updateDocument.mutate(
      {
        id: document.id,
        form: form.getValues(),
        originalCollectionId: document.collectionId,
        applyRoleChanges,
      },
      { onSuccess: () => onOpenChange(false) },
    )
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl h-[85vh] p-0 flex flex-col overflow-hidden gap-0">
        <DialogHeader className="p-6 pb-4 border-b">
          <DialogTitle>Edit document</DialogTitle>
        </DialogHeader>

        <div className="border-b px-6 py-4">
          <UploadWizardSteps
            steps={EDIT_WIZARD_STEPS}
            currentStep={currentStep}
            // Editing starts from a fully-populated document — every step already has valid
            // data, so all are freely navigable rather than gated behind linear progression
            // (unlike Upload, where later steps depend on earlier ones being filled in first).
            completedSteps={Object.fromEntries(STEP_KEYS.map((step) => [step, true]))}
            onSelectStep={(step) => setCurrentStep(step as EditWizardStep)}
          />
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          {currentStep === 'details' && <EditDetailsStep form={form} />}
          {currentStep === 'georeference' && (
            <GeoreferenceStep
              value={form.watch('georeference')}
              onChange={(value) => form.setValue('georeference', value, { shouldDirty: true })}
            />
          )}
          {currentStep === 'permissions' && (
            <PermissionsStep
              visibility={visibility}
              roleIds={roleIds}
              onChange={({ visibility, roleIds }) => {
                form.setValue('visibility', visibility, { shouldDirty: true })
                form.setValue('roleIds', roleIds, { shouldValidate: true, shouldDirty: true })
              }}
              rolesError={
                requiresRoleSelection
                  ? rolesRequiredMessage
                  : form.formState.errors.roleIds?.message
              }
              existingRolesUnknown={permissionsFetchFailed}
            />
          )}
        </div>

        <div className="flex justify-between p-4 border-t">
          <Button
            type="button"
            variant="outline"
            onClick={() => setCurrentStep(STEP_KEYS[currentIndex - 1])}
            disabled={currentIndex === 0}
          >
            Back
          </Button>
          <div className="flex gap-2">
            {currentIndex < STEP_KEYS.length - 1 && (
              <Button
                type="button"
                variant="outline"
                onClick={() => setCurrentStep(STEP_KEYS[currentIndex + 1])}
              >
                Next
              </Button>
            )}
            <Button type="button" onClick={handleSave} disabled={!canSave}>
              {updateDocument.isPending ? 'Saving…' : 'Save'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
